import React, { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaTiktok } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import "./Footer.css";
import logo from "../../assets/images/logo 2.png";
import visaLogo from "../../assets/images/pament method/visa.svg";
import tabbyLogo from "../../assets/images/pament method/tabby.svg";
import tamaraLogo from "../../assets/images/pament method/tamara.svg";
import moyasarLogo from "../../assets/images/payment/moyasar.svg";
import { useTranslation } from "../../contexts/TranslationContext";
import { axiosGet } from "../../utils/apis/api";

const COMPANY_VALUES = {
  commercialRegistration: "7016007630",
  vatNumber: "310704710300003",
  tourismLicense: "73100844",
  email: "Info@alltimestravels.com",
};

const LOCALIZED_COPY = {
  en: {
    companyInformation: "Company Information",
    companyName: "All Times Travel and Tourism Agency",
    addressLabel: "Address",
    address: "Al Urubah Rd. 3516, Al Woroud Dist. 6383, Riyadh",
    commercialRegistration: "CR. No.",
    vatNumber: "VAT No.",
    tourismLicense: "Tourism License No.",
    category: "Category",
    travelAgents: "Travel Agents",
    email: "Email",
    copyright: "All Times. All rights reserved.",
  },
  ar: {
    companyInformation: "معلومات الشركة",
    companyName: "وكالة كل الأوقات للسفر والسياحة",
    addressLabel: "العنوان",
    address: "طريق العروبة 3516، حي الورود 6383، الرياض",
    commercialRegistration: "السجل التجاري",
    vatNumber: "الرقم الضريبي",
    tourismLicense: "رخصة السياحة",
    category: "الفئة",
    travelAgents: "وكالات السفر",
    email: "البريد الإلكتروني",
    copyright: "كل الأوقات. جميع الحقوق محفوظة.",
  },
};

const FooterColumn = ({ title, children, className = "" }) => (
  <div className={`footer-text ${className}`.trim()}>
    <h3>{title}</h3>
    {children}
  </div>
);

const BusinessDetail = ({ label, children, className = "" }) => (
  <div className={`business-detail ${className}`.trim()}>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const CompanyDetails = ({ copy }) => {
  return (
    <section
      className="company-details company-details--band"
      aria-label={copy.companyInformation}
    >
      <div className="company-details__content">
        <div className="company-details__group company-details__identity">
          <p className="company-details__name">{copy.companyName}</p>
          <dl>
            <BusinessDetail label={copy.addressLabel}>
              {copy.address}
            </BusinessDetail>
            <BusinessDetail label={copy.email}>
              <a href={`mailto:${COMPANY_VALUES.email}`}>
                <bdi dir="ltr">{COMPANY_VALUES.email}</bdi>
              </a>
            </BusinessDetail>
          </dl>
        </div>

        <dl className="company-details__group company-details__registration">
          <BusinessDetail label={copy.commercialRegistration}>
            <bdi dir="ltr">{COMPANY_VALUES.commercialRegistration}</bdi>
          </BusinessDetail>
          <BusinessDetail label={copy.vatNumber}>
            <bdi dir="ltr">{COMPANY_VALUES.vatNumber}</bdi>
          </BusinessDetail>
        </dl>

        <dl className="company-details__group company-details__license">
          <BusinessDetail label={copy.tourismLicense}>
            <bdi dir="ltr">{COMPANY_VALUES.tourismLicense}</bdi>
          </BusinessDetail>
          <BusinessDetail label={copy.category}>
            {copy.travelAgents}
          </BusinessDetail>
        </dl>
      </div>
    </section>
  );
};

const BrandColumn = ({ copy, footerText }) => (
  <div className="footer-text footer-brand">
    <Link to="/home" aria-label={copy.companyName}>
      <img className="footer-brand__logo" src={logo} alt="All Times Travel Agency" />
    </Link>
    <p className="footer-brand__description">{footerText}</p>
  </div>
);

const PaymentMethods = ({ title }) => (
  <FooterColumn title={title} className="footer-payment">
    <div className="footer-payment__logos" aria-label={title}>
      <img src={visaLogo} alt="Visa" />
      <img src={tabbyLogo} alt="Tabby" />
      <img src={tamaraLogo} alt="Tamara" />
      <img src={moyasarLogo} alt="Moyasar" />
    </div>
  </FooterColumn>
);

const SocialLinks = ({ links }) => {
  const items = [
    { key: "x_link", label: "X", Icon: FaXTwitter },
    { key: "tiktok_link", label: "TikTok", Icon: FaTiktok },
    { key: "facebook_link", label: "Facebook", Icon: FaFacebookF },
    { key: "instagram_link", label: "Instagram", Icon: FaInstagram },
  ];
  const visibleItems = items.filter(({ key }) => links[key]);

  if (!visibleItems.length) return null;

  return (
    <div className="footer-social-links">
      {visibleItems.map(({ key, label, Icon }) => (
        <a
          key={key}
          href={links[key]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
        >
          <Icon aria-hidden="true" />
        </a>
      ))}
    </div>
  );
};

const StandardLinks = ({ t }) => (
  <>
    <FooterColumn title={t("SiteMap")}>
      <Link to="/categories">{t("Packages")}</Link>
    </FooterColumn>
    <FooterColumn title={t("LearnMore")}>
      <Link to="/about">{t("AboutUs")}</Link>
      <Link to="/contact">{t("ContactUs")}</Link>
    </FooterColumn>
    <FooterColumn title={t("Support")}>
      <Link to="/terms">{t("Terms")}</Link>
      <Link to="/privacy">{t("PrivacyPolicy")}</Link>
    </FooterColumn>
  </>
);

const Footer = () => {
  const { t, currentLanguage } = useTranslation();
  const [socialLinks, setSocialLinks] = useState({
    instagram_link: "",
    facebook_link: "",
    tiktok_link: "",
    x_link: "",
  });
  const currentYear = new Date().getFullYear();
  const copy = LOCALIZED_COPY[currentLanguage === "ar" ? "ar" : "en"];

  const footerText = useMemo(() => {
    const translated = t("FooterText");
    return translated === "FooterText" ? "" : translated;
  }, [t]);

  useEffect(() => {
    let isMounted = true;
    const normalizeLink = (link) =>
      typeof link === "string" && link.trim() ? link.trim() : "";

    const fetchSocialLinks = async () => {
      try {
        const response = await axiosGet(
          "api/method/alltimes.www.api.social_media_links"
        );
        const result = response?.result || {};

        if (isMounted) {
          setSocialLinks({
            instagram_link: normalizeLink(result.instagram_link),
            facebook_link: normalizeLink(result.facebook_link),
            tiktok_link: normalizeLink(result.tiktok_link),
            x_link: normalizeLink(result.x_link),
          });
        }
      } catch (error) {
        console.error("Failed to fetch social media links", error);
      }
    };

    fetchSocialLinks();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <footer className="site-footer">
      <Container>
        <div className="footer-content">
          <BrandColumn copy={copy} footerText={footerText} />
          <StandardLinks t={t} />
          <PaymentMethods title={t("PaymentMethod")} />
        </div>
        <CompanyDetails copy={copy} />

        <div className="footer-bottom">
          <div className="footer-bottom__content">
            <div className="footer-bottom__legal-links">
              <Link to="/privacy">{t("PrivacyPolicy")}</Link>
              <Link to="/terms">{t("Terms")}</Link>
            </div>
            <p>
              © {currentYear} {copy.copyright}
            </p>
            <SocialLinks links={socialLinks} />
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
